import React, { useEffect, useMemo, useState } from 'react'
import { Button } from 'primereact/button'
import { confirmDialog } from 'primereact/confirmdialog'

import { Toggle } from '../../common/components/Toggle'
import {
    ChargePoint,
    Connector,
    KeyValue,
    GreenChargingChargePointStatus,
    useSmartChargingScheduleGetByChargePointQuery,
    useOcppCommandUpdateConfigKeyMutation,
    OnOffMode,
} from '../../common/redux/api/web-api'
import Text from '../../ui/Text'
import findOcppConfig from '../findOcppConfig'
import { getConfigValue } from '../get-config-value'
import { RemoteControlRow } from '../remote-control-row'
import setOcppConfigValue from '../setOcppConfigValue'
import { remoteStartIsAvailable, remoteStopIsAvailable } from '../get-connector-state'
import { isUserappDomain } from '../currentDomain'
import { onGoingTransactionStates } from '../charge-point-dawn-control-widget'
import useText from '../../common/hooks/useText'
import { useToast } from '../../common/uikit/ToastProvider'
import { SelectButton } from '../../common/uikit/SelectButton'

export const KEY_FREE_CHARGING = 'FreeCharging'
export const KEY_CABLE_LOCK = 'CableLock'

export enum NfcLockState {
    ON = '0',
    OFF = '1',
}

export enum CableLockState {
    ON = '1',
    OFF = '0',
}

export type OcppAuraConnectorControlProps = {
    connector: Connector
    isOnline: boolean
    greenChargingStatus?: GreenChargingChargePointStatus['greenChargingStatus']
    chargePoint: ChargePoint
    isReadOnly: boolean
    onRemoteControl: ({ connectorId, isNfcLockOn }: { connectorId: number; isNfcLockOn: boolean }) => void
    handleOverrideSchedule: (connectorId: number) => void
    onConnectorUpdate?: (connectorId: number, updatedConnector: Connector) => void
}

export function OcppAuraConnectorControl({
    connector,
    isOnline,
    chargePoint,
    isReadOnly,
    onRemoteControl,
    handleOverrideSchedule,
    onConnectorUpdate,
}: OcppAuraConnectorControlProps): React.ReactElement {
    const [configKeys, setConfigKeys] = useState<KeyValue[] | null | undefined>(chargePoint.ocppConfigKeys)
    const toast = useToast()

    const text = useText()

    const { data: schedules } = useSmartChargingScheduleGetByChargePointQuery({ chargePointId: chargePoint.id })

    const connectorHasSchedule = (connectorId: number) => {
        return schedules?.some((schedule) => schedule.connectorId === connectorId)
    }

    useEffect(() => {
        setConfigKeys(chargePoint.ocppConfigKeys)
    }, [chargePoint])

    const [saveConfigKey] = useOcppCommandUpdateConfigKeyMutation()

    const nfcLockValue = getConfigValue(KEY_FREE_CHARGING, configKeys, connector.connectorId)
    const isNfcLockOn = nfcLockValue ? nfcLockValue === NfcLockState.ON : false

    const cableLockValue = getConfigValue(KEY_CABLE_LOCK, configKeys, connector.connectorId)
    const isCableLockOn = cableLockValue ? cableLockValue === CableLockState.ON : false

    const remoteStartIsVisible = useMemo(() => remoteStartIsAvailable(connector), [connector])
    const remoteStopIsVisible = useMemo(() => remoteStopIsAvailable(connector), [connector])

    const setConnectorOcppConfigValue = async (key: string, value: string) => {
        const prevValue = findOcppConfig(key, configKeys)
        if (!(prevValue && value && chargePoint)) return

        const values = prevValue.split(',')
        values[connector.connectorId - 1] = `${connector.connectorId}.${value}`
        const updatedConfigKeys = setOcppConfigValue(key, values.join(','), configKeys)
        if (!updatedConfigKeys) return

        await saveConfigKey({
            chargePointId: chargePoint.id,
            keyValue: updatedConfigKeys.find((configKeys) => configKeys.key === key),
        })
            .unwrap()
            .then(() => setConfigKeys(updatedConfigKeys))
            .catch(() => toast.notifyError(text('msgRemoteControlCommandFailed')))
    }

    const showStartStopChargingButton = !isUserappDomain && isOnline && (remoteStartIsVisible || remoteStopIsVisible)
    const showIgnoreScheduleButton =
        !isUserappDomain && isOnline && remoteStopIsVisible && connector?.mode === 'Schedule'

    const handleConnectorStateChange = (connectorId: number) => async (state: OnOffMode) => {
        if (state === 'Schedule' && !connectorHasSchedule(connectorId)) {
            return confirmDialog({
                header: text('msgNoSchedulesHeader'),
                message: text('msgNoSchedulesText'),
                acceptClassName: 'hidden',
                rejectLabel: text('btnClose'),
            })
        }

        const updatedConnector = { ...connector, mode: state }
        onConnectorUpdate?.(connectorId, updatedConnector)
    }

    return (
        <>
            <RemoteControlRow
                button={
                    <SelectButton<OnOffMode>
                        id={`connector-mode-${chargePoint.id}-${connector.connectorId}`}
                        ariaLabel={text(connector.connectorId === 1 ? 'lblLeftSocket' : 'lblRightSocket')}
                        options={[
                            { label: text('lblOff'), value: 'Off' },
                            { label: text('lblConnectorSchedule'), value: 'Schedule' },
                            { label: text('lblConnectorAlwaysOn'), value: 'On' },
                        ]}
                        value={connector.mode}
                        onChange={handleConnectorStateChange(connector.connectorId ?? 1)}
                        disabled={isReadOnly || !isOnline || chargePoint?.ocppEnabled}
                        className="w-20rem max-w-full"
                        data-testid={`charger-${chargePoint.id}-connector-${connector.connectorId}`}
                    />
                }
                title={
                    <div data-testid={`charger-${chargePoint.id}-connector-label-${connector.connectorId}`}>
                        <Text code={connector.connectorId === 1 ? 'lblLeftSocket' : 'lblRightSocket'} />
                    </div>
                }
                remoteChargingControl={
                    <div className="flex gap-2 flex-column sm:flex-row">
                        {showStartStopChargingButton && (
                            <Button
                                size="small"
                                data-testid={`remote-control-button-${connector.connectorId}`}
                                onClick={() =>
                                    onRemoteControl({ connectorId: connector.connectorId, isNfcLockOn: isNfcLockOn })
                                }
                                disabled={!isOnline}
                                label={text(remoteStopIsVisible ? 'lblStop' : 'lblStart')}
                                outlined
                                tooltip={remoteStartIsVisible ? text('lblStartChargingInfo') : text('lblStopSession')}
                                severity="info"
                                icon={remoteStopIsVisible ? 'pi pi-stop-circle' : 'pi pi-play-circle'}
                            />
                        )}
                        {showIgnoreScheduleButton && (
                            <Button
                                tooltip={text('lblIgnoreSchedule')}
                                style={{ padding: '6px' }}
                                data-testid={`ignore-schedule-button-${connector.connectorId}`}
                                onClick={() => handleOverrideSchedule(connector.connectorId)}
                                outlined
                                severity="info"
                                icon="pi pi-calendar-times"
                            />
                        )}
                    </div>
                }
            />

            <RemoteControlRow
                button={
                    <Toggle
                        toggleFn={() =>
                            setConnectorOcppConfigValue(
                                KEY_FREE_CHARGING,
                                nfcLockValue === NfcLockState.ON ? NfcLockState.OFF : NfcLockState.ON
                            )
                        }
                        disabled={
                            isReadOnly ||
                            onGoingTransactionStates.includes(connector?.ocppStatus) ||
                            !isOnline ||
                            chargePoint?.ocppEnabled
                        }
                        isToggled={isNfcLockOn}
                        data-testid={`rfid-${chargePoint.id}-connector-${connector.connectorId}`}
                    />
                }
                title={
                    <div data-testid={`rfid-label-${chargePoint.id}-connector-${connector.connectorId}`}>
                        <Text code="lblRFIDLock" />
                    </div>
                }
            />
            <RemoteControlRow
                button={
                    <Toggle
                        toggleFn={() =>
                            setConnectorOcppConfigValue(
                                KEY_CABLE_LOCK,
                                cableLockValue === CableLockState.ON ? CableLockState.OFF : CableLockState.ON
                            )
                        }
                        disabled={!isOnline || chargePoint?.ocppEnabled || isReadOnly}
                        isToggled={isCableLockOn}
                        data-testid={`cable-lock-toggle-${chargePoint.id}-connector-${connector.connectorId}`}
                    />
                }
                title={
                    <div data-testid={`cable-lock-label-${chargePoint.id}-connector-${connector.connectorId}`}>
                        <Text code="lblCableLock" />
                    </div>
                }
            />
        </>
    )
}
